import { useCallback, useEffect, useState } from "react";
import api from "./api";

// Owner view of what the AI thinks the photo is (Photo/Character/Pet) plus the body-shape state: what is applied,
// what is queued behind the spend gate and whether the picker is locked by an existing sculpt.
export function useSubject(creationId) {
  const [subject, setSubject] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/creations/${creationId}/subject`);
      setSubject(data);
      return data;
    } catch {
      setSubject(null);
      return null;
    }
  }, [creationId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll only while a body-shape change is waiting for the gate, so the customer sees it land by itself.
  useEffect(() => {
    if (subject?.pending?.state !== "queued") return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [subject?.pending?.state, load]);

  const patch = useCallback(
    async (body, key) => {
      setBusy(key || Object.keys(body)[0]);
      try {
        const { data } = await api.patch(`/creations/${creationId}/spec`, body);
        const fresh = await load();
        return { creation: data, subject: fresh };
      } catch (e) {
        await load();            // a change parked behind the spend gate shows up as pending/queued on the subject
        throw e;
      } finally {
        setBusy(null);
      }
    },
    [creationId, load],
  );

  return { subject, busy, patch, reload: load };
}
